/**
 * Spec 105 — PersonasPage. Brand-scoped persona list + create + detail.
 *
 * Lists the selected brand's personas as cards (name, type badge, confidence
 * badge, dimension-family counts), with a "New persona" button. Opening a card
 * shows the PersonaDetailView with a "New version" / "Deactivate" action. All
 * mutation controls are hidden for non-admins (the API also 403s them).
 *
 * The whole surface is feature-flagged server-side (`feature.personas`); when
 * off, the list call returns 404 and the page shows a disabled-surface state.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { useAuth } from '../lib/auth-context.js';
import { useToast } from '../hooks/useToast.js';
import {
  listPersonas,
  PersonaSurfaceDisabledError,
  getPersona,
  createPersona,
  createPersonaVersion,
  deactivatePersona,
  reactivatePersona,
  PERSONA_DIMENSION_TYPES,
} from '../api/personas.js';
import type { Persona, PersonaCreateInput } from '../api/personas.js';
import { PersonaTypeBadge } from '../components/PersonaTypeBadge.js';
import { PersonaConfidenceBadge } from '../components/PersonaConfidenceBadge.js';
import { PersonaFormModal } from '../components/PersonaFormModal.js';
import { PersonaDetailView } from '../components/PersonaDetailView.js';
import { Button } from '../components/ui/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { RowList, Row, RowFacts, RowFact } from '../components/ui/Row.js';
import { Chip } from '../components/ui/Chip.js';

export function PersonasPage() {
  const { selectedBrand } = useBrand();
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const brandId = selectedBrand?.id ?? null;

  const canMutate = useMemo(
    () => hasRole('rule_admin') || hasRole('orbit:admin'),
    [hasRole],
  );
  // Feature flags are an orbit:admin page (AdminHomePage / FeatureFlagsPage).
  const isAdmin = hasRole('orbit:admin');

  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [surfaceOff, setSurfaceOff] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selected, setSelected] = useState<Persona | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [versionBase, setVersionBase] = useState<Persona | null>(null);

  const refresh = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    setSurfaceOff(false);
    try {
      const rows = await listPersonas(brandId, includeInactive);
      setPersonas(rows);
    } catch (e) {
      if (e instanceof PersonaSurfaceDisabledError) setSurfaceOff(true);
      else setError(e instanceof Error ? e.message : 'Failed to load personas');
      setPersonas([]);
    } finally {
      setLoading(false);
    }
  }, [brandId, includeInactive]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleCreate(input: PersonaCreateInput) {
    if (!brandId) return;
    const created = await createPersona(brandId, input);
    setShowForm(false);
    addToast(`Persona "${created.name}" created (v1)`, 'success');
    await refresh();
  }

  async function handleNewVersion(input: PersonaCreateInput) {
    if (!brandId || !versionBase) return;
    const updated = await createPersonaVersion(
      brandId,
      versionBase.persona_id,
      input,
      versionBase.version,
    );
    setVersionBase(null);
    setSelected(updated);
    addToast(`New version v${updated.version} created`, 'success');
    await refresh();
  }

  async function openDetail(p: Persona) {
    if (!brandId) return;
    try {
      const full = await getPersona(brandId, p.persona_id);
      setSelected(full);
    } catch {
      setSelected(p);
    }
  }

  async function handleDeactivate() {
    if (!brandId || !selected) return;
    try {
      const updated = await deactivatePersona(brandId, selected.persona_id);
      setSelected(updated);
      addToast('Persona deactivated', 'success');
      await refresh();
    } catch (e) {
      addToast(
        e instanceof Error ? e.message : 'Failed to deactivate',
        'error',
      );
    }
  }

  async function handleReactivate() {
    if (!brandId || !selected) return;
    try {
      const updated = await reactivatePersona(brandId, selected.persona_id);
      setSelected(updated);
      addToast('Persona reactivated', 'success');
      await refresh();
    } catch (e) {
      addToast(
        e instanceof Error ? e.message : 'Failed to reactivate',
        'error',
      );
    }
  }

  if (!brandId) {
    return (
      <div className="hub-page--table">
        <p className="text-sm text-[var(--hub-color-ink-muted)]">
          Select a brand to view its personas.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="personas-page" className="hub-page--table">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Personas</h1>
          <p className="text-sm text-gray-500">
            Brand-scoped buyer personas for persona-aware evaluation.
          </p>
        </div>
        {canMutate && !surfaceOff && (
          <Button
            variant="primary"
            data-testid="persona-new-button"
            onClick={() => {
              setVersionBase(null);
              setShowForm(true);
            }}
          >
            New persona
          </Button>
        )}
      </div>

      {/* Personas are behind a deployment flag. With it off, say so — the page
          used to print "API error: 404" under a New persona button that could
          only fail the same way. */}
      {surfaceOff && (
        <EmptyState
          icon="bx-user"
          title="Personas are switched off"
          description={canMutate
            ? 'Personas are turned off for this deployment. Turn on feature.personas in Feature flags to use them.'
            : 'Personas are turned off for this deployment. Ask an admin to turn them on.'}
          actionLabel={isAdmin ? 'Open feature flags' : undefined}
          actionHref={isAdmin ? '/admin/feature-flags' : undefined}
          testId="personas-surface-off"
        />
      )}

      {!surfaceOff && (
      <label className="mb-4 flex items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          data-testid="persona-include-inactive"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Include deactivated
      </label>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-gray-400">Loading…</p>}

      {!loading && personas.length === 0 && !error && !surfaceOff && (
        <p
          data-testid="personas-empty"
          className="text-sm text-gray-400"
        >
          No personas yet.
        </p>
      )}

      {personas.length > 0 && (
        // Spec 235 §3.2 — lg:grid-cols-3 here is a tray of identical tiles,
        // not columns that mean something (contrast ComparisonPage's kept
        // grid): decorative, so it converts to rows. Each tile's action was
        // "open this persona inline" (setSelected — no route change, no
        // /personas/:id), never a navigation, so there is no `to` to give
        // Row; the whole tile used to be one big <button> with nothing
        // nested inside it, so the click affordance narrows to the title
        // itself rather than inventing a new "clickable but not navigating"
        // row variant for a single page's one-off need.
        <RowList>
          {personas.map((p) => {
            const counts = PERSONA_DIMENSION_TYPES.map((f) => ({
              f,
              n: p.dimensions.filter((d) => d.dimension_type === f).length,
            })).filter((c) => c.n > 0);
            return (
              <Row
                key={p.persona_id}
                data-testid="persona-card"
                data-persona-id={p.persona_id}
                className={p.is_active ? '' : 'opacity-60'}
                title={
                  <button
                    type="button"
                    className="hub-row__link text-left"
                    onClick={() => void openDetail(p)}
                  >
                    {p.name} <span className="text-xs text-gray-400">v{p.version}</span>
                  </button>
                }
                top={
                  <>
                    <PersonaTypeBadge personaType={p.persona_type} />
                    <PersonaConfidenceBadge confidence={p.confidence} />
                    {!p.is_active && <Chip>Deactivated</Chip>}
                  </>
                }
              >
                <RowFacts>
                  {counts.map((c) => (
                    <RowFact key={c.f}>{c.n} {c.f}</RowFact>
                  ))}
                </RowFacts>
              </Row>
            );
          })}
        </RowList>
      )}

      {selected && (
        <div className="mt-6">
          <Button variant="quiet" className="mb-2" onClick={() => setSelected(null)}>
            ← Back to list
          </Button>
          <PersonaDetailView
            persona={selected}
            canMutate={canMutate}
            onNewVersion={() => {
              setVersionBase(selected);
              setShowForm(true);
            }}
            onDeactivate={() => void handleDeactivate()}
            onReactivate={() => void handleReactivate()}
          />
        </div>
      )}

      {showForm && (
        <PersonaFormModal
          brandId={brandId}
          base={versionBase}
          onClose={() => {
            setShowForm(false);
            setVersionBase(null);
          }}
          onSubmit={versionBase ? handleNewVersion : handleCreate}
        />
      )}
    </div>
  );
}
